import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion } from 'framer-motion';

const Header = () => {
  const [isHovered, setIsHovered] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isHomePage = location.pathname === '/';

  const scrollToSection = (sectionId: string) => {
    // Redirect Platform to external URL
    if (sectionId === 'platform') {
      window.location.href = 'https://uat.marvinenergy.ai';
      return;
    }
    
    if (!isHomePage) {
      // Navigate to home page first, then scroll
      window.location.href = `/#${sectionId}`;
      return;
    }
    
    const element = document.getElementById(sectionId);
    if (element) {
      // Close mobile menu if open
      setIsMobileMenuOpen(false);
      
      // Use Lenis for smooth scrolling if available, otherwise fallback to browser smooth scroll
      const lenis = (window as { lenis?: { scrollTo: (element: Element, options: { duration: number; easing: (t: number) => number }) => void } }).lenis;
      if (lenis) {
        lenis.scrollTo(element, {
          duration: 1.5,
          easing: (t: number) => 1 - Math.pow(1 - t, 3), // Ease-out cubic
        });
      } else {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const navigationItems = [
    { label: 'Services', id: 'services' },
    { label: 'Technology', id: 'technology' },
    { label: 'References', id: 'references' },
    { label: 'About Us', id: 'about' },
    { label: 'Platform', id: 'platform' },
  ];

  const headerClasses = `
    fixed top-0 left-0 right-0 z-50 transition-all duration-300 ease-in-out
    ${isHovered || !isHomePage 
      ? 'bg-primary-bg/95 backdrop-blur-sm shadow-lg' 
      : 'bg-transparent backdrop-blur-md'
    }
  `;

  return (
    <motion.header 
      className={headerClasses}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src="/media/logo.png" 
              alt="Marvin AI Logo" 
              className="w-8 h-8 rounded-lg"
            />
            <span className="text-xl font-display font-semibold text-white text-shadow">
              Marvin AI
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navigationItems.map((item) => (
                              <button
                  key={item.label}
                  onClick={() => scrollToSection(item.id)}
                  className="text-white/90 hover:text-primary-cyan transition-colors duration-200 text-shadow font-medium"
                >
                  {item.label}
                </button>
            ))}
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <motion.nav 
            className="md:hidden mt-4 pb-4 border-t border-white/10"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex flex-col space-y-3 pt-4">
              {navigationItems.map((item) => (
                <button
                  key={item.label}
                  onClick={() => {
                    scrollToSection(item.id);
                    setIsMobileMenuOpen(false);
                  }}
                  className="text-white/90 hover:text-primary-cyan transition-colors duration-200 text-left py-2"
                >
                  {item.label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </div>
    </motion.header>
  );
};

export default Header;

